import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { loadAuthedMedia } from "./useAuthedMedia";

/** Height of the collapsed email preview, in px. */
const PREVIEW_HEIGHT = 320;

export const looksLikeHtml = (text) => /<\/?[a-z][\s\S]*>/i.test(text || "");

const stripAngles = (value) => String(value || "").replace(/^<|>$/g, "");

/**
 * Inline images arrive as `cid:` references to attachments that sit behind
 * auth; fetch them through axios and swap in object URLs the frame can load.
 */
const useInlineImages = (html, attachments) => {
  const [urls, setUrls] = useState({});

  useEffect(() => {
    const inline = (attachments || []).filter(
      (a) => a.content_id && a.url && html && html.includes(`cid:${stripAngles(a.content_id)}`)
    );
    if (!inline.length) return undefined;
    let active = true;
    Promise.all(
      inline.map((a) =>
        loadAuthedMedia(a.url)
          .then((url) => [stripAngles(a.content_id), url])
          .catch(() => null)
      )
    ).then((pairs) => {
      if (active) setUrls(Object.fromEntries(pairs.filter(Boolean)));
    });
    return () => {
      active = false;
    };
  }, [html, attachments]);

  return urls;
};

/**
 * An HTML email as it was sent. An iframe, not innerHTML: a full email
 * document brings its own <style> and <body>, which would otherwise restyle
 * this page (or, flattened to text, show up as raw CSS). No allow-scripts, so
 * allow-same-origin only lets us measure the content to size the frame.
 */
const HtmlEmailFrame = ({ html, attachments, previewHeight = PREVIEW_HEIGHT }) => {
  const frameRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(previewHeight);
  const [expanded, setExpanded] = useState(false);
  const inlineUrls = useInlineImages(html, attachments);

  // Links open in a new tab instead of inside the bubble.
  const srcDoc = useMemo(() => {
    let body = html || "";
    Object.entries(inlineUrls).forEach(([cid, url]) => {
      body = body.split(`cid:${cid}`).join(url);
    });
    return (
      '<base target="_blank">' +
      "<style>html,body{margin:0;padding:8px;font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;" +
      "font-size:14px;word-break:break-word;}img{max-width:100%;height:auto;}</style>" +
      body
    );
  }, [html, inlineUrls]);

  const measure = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.documentElement) return;
    setContentHeight(Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0));
  }, []);

  // Images load after onLoad fires; keep the frame sized to its content.
  const observerRef = useRef(null);
  const handleLoad = useCallback(() => {
    measure();
    observerRef.current?.disconnect();
    const doc = frameRef.current?.contentDocument;
    if (doc?.body && typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(doc.body);
    }
  }, [measure]);
  useEffect(() => () => observerRef.current?.disconnect(), []);

  const isLong = contentHeight > previewHeight;

  return (
    <div>
      <iframe
        ref={frameRef}
        title="Email body"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={srcDoc}
        onLoad={handleLoad}
        style={{
          width: "100%",
          height: expanded || !isLong ? contentHeight : previewHeight,
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          background: "#fff",
          display: "block",
        }}
      />
      {isLong && (
        <a style={{ fontSize: 12 }} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "show less" : "show more"}
        </a>
      )}
    </div>
  );
};

export default HtmlEmailFrame;
